import React, { useState, useEffect } from "react";
import { Eye, Plus, X, Filter } from "lucide-react";
import { NavLink } from "react-router-dom/cjs/react-router-dom";
import Loading from "../../component/Loading/Loading";
import { useSelector } from "react-redux";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const PlanList = () => {
  const axiosInstance = useAxiosInstance();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [planPeriodOptions, setPlanPeriodOptions] = useState([]);
  const [planRangeOptions, setPlanRangeOptions] = useState([]);

  const [selectedPlanType, setSelectedPlanType] = useState("");
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState("");
  const [selectedPlanRange, setSelectedPlanRange] = useState("");

  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    fetchSelectOptions();
  }, [selectedPlanType, selectedPlanPeriod, selectedPlanRange]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/public/plan/getAllPlansWithPlanRangeTypePeriod`,
        {
          params: {
            planType: selectedPlanType,
            planPeriod: selectedPlanPeriod,
            planRange: selectedPlanRange,
          },
        }
      );
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectOptions = async () => {
    try {
      const periodResponse = await axiosInstance.get(
        `/api/public/plan-period/all`
      );
      setPlanPeriodOptions(
        periodResponse.data.map((period) => ({
          value: period.planPeriod,
        }))
      );

      const rangeResponse = await axiosInstance.get(
        `/api/public/plan-range/all`
      );
      setPlanRangeOptions(
        rangeResponse.data.map((range) => ({
          value: range.planRange,
        }))
      );

      const typeResponse = await axiosInstance.get(
        `/api/public/plan-type/all`
      );
      setPlanTypeOptions(
        typeResponse.data.map((type) => ({
          value: type.planType,
        }))
      );
    } catch (error) {
      console.error("Error fetching select options:", error);
    }
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const closeViewModal = () => {
    setSelectedPlan(null);
  };

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axiosInstance.delete(`/api/public/plan/delete/${planToDelete.planId}`);
      fetchPlans();
      setShowDeleteModal(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error("Error deleting plan:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlanType = (event) => {
    setSelectedPlanType(event.target.value);
  };

  const handleChangePlanPeriod = (event) => {
    setSelectedPlanPeriod(event.target.value);
  };

  const handleChangePlanRange = (event) => {
    setSelectedPlanRange(event.target.value);
  };

  const filteredPlans = plans.filter(
    (plan) =>
      (!selectedPlanType || plan.planType === selectedPlanType) &&
      (!selectedPlanPeriod || plan.planPeriod === selectedPlanPeriod) &&
      (!selectedPlanRange || plan.planRange === selectedPlanRange)
  );

  const clearFilters = () => {
    setSelectedPlanPeriod("");
    setSelectedPlanType("");
    setSelectedPlanRange("");
  };

  // View modal footer with close button
  const viewModalFooter = (
    <div className="flex justify-end">
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={closeViewModal}
      >
        Close
      </button>
    </div>
  );

  // Delete modal footer with action buttons
  const deleteModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
        onClick={confirmDelete}
      >
        Delete
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Plan List</h1>
            <NavLink
              to="/add-plan"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Add Plan Details</span>
            </NavLink>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:justify-end items-end gap-4">
                <div className="w-full md:w-auto flex flex-wrap gap-2 justify-end">
                  <select
                    value={selectedPlanRange}
                    onChange={handleChangePlanRange}
                    className="flex-1 md:w-fit px-3 py-[11px] border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="">Plan Range</option>
                    {planRangeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedPlanPeriod}
                    onChange={handleChangePlanPeriod}
                    className="flex-1 md:w-fit px-3 py-[11px] border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="">Plan Period</option>
                    {planPeriodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedPlanType}
                    onChange={handleChangePlanType}
                    className="w-full md:w-fit px-3 py-[11px] border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="">Plan Type</option>
                    {planTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                  </select>
                <button
                  onClick={clearFilters}
                  className="flex-1 px-4 py-[11px] bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
                  title="Clear Filter"
                >
                  Clear
                </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Range</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Period</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Type</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPlans.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    filteredPlans.map((plan, index) => (
                      <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{plan.planRange}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{plan.planPeriod}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{plan.planType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                          <button
                            className="text-blue-500 hover:text-blue-700"
                            onClick={() => handleViewPlan(plan)}
                            title="View Plan"
                          >
                            <Eye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* View Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={!!selectedPlan}
            onClose={closeViewModal}
            title="View Plan Details"
            size="md"
            footer={viewModalFooter}
          >
            {selectedPlan && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Range</label>
                  <input
                    type="text"
                    value={selectedPlan.planRange}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Period</label>
                  <input
                    type="text"
                    value={selectedPlan.planPeriod}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
                  <input
                    type="text"
                    value={selectedPlan.planType}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
              </div>
            )}
          </GlassmorphicModal>

          {/* Delete Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showDeleteModal}
            onClose={closeModal}
            title="Confirm Delete"
            size="sm"
            footer={deleteModalFooter}
          >
            <p className="text-gray-700">
              Are you sure you want to delete this plan? This action cannot be undone.
            </p>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default PlanList;